import React, {Component} from 'react';
import {Page} from 'framework7-react';
import './TrainingVideosPopup.css';
import PopupComponent from '../popup/Popup';
import axios from 'axios';
import {isCordova} from '../../../utils/isCordova';
import Label from '../../label/Label';
import PrimaryButton from '../../primaryButton/PrimaryButton';
import Input from '../../input/Input';
import getBaseUrl from "../../../api/getBaseUrl";

export default class TrainingVideosPopup extends Component {
	constructor(props) {
		super(props);
		this.state = {
			videos: [],
			loading: true,
			error: null,
			selectedVideo: null,
			selectedVideoData: null,
			searchTerm: ''
		};
		this.videoRef = React.createRef();
	}

	componentDidMount() {
		this.fetchVideos();

		// Close popup on ESC key press
		document.addEventListener('keydown', this.handleKeyDown);

		// Additional handling for Cordova video playback
		if (this.videoRef.current) {
			const video = this.videoRef.current;
			
			// Prevent fullscreen on play
			video.addEventListener('webkitbeginfullscreen', (e) => {
				e.preventDefault();
				video.webkitExitFullscreen();
			});
			
			// Handle presentation mode changes
			video.addEventListener('webkitpresentationmodechanged', (e) => {
				if (video.webkitPresentationMode === 'fullscreen') {
					video.webkitSetPresentationMode('inline');
				}
			});
			
			// Set additional properties for inline playback
			video.setAttribute('webkit-playsinline', 'true');
			video.setAttribute('playsinline', 'true');
			video.setAttribute('x-webkit-airplay', 'allow');
			
			// Force inline mode
			if (video.webkitSetPresentationMode) {
				video.webkitSetPresentationMode('inline');
			}
		}
	}

	componentWillUnmount() {
		document.removeEventListener('keydown', this.handleKeyDown);
	}

	handleKeyDown = (e) => {
		if (e.key === 'Escape' && this.state.selectedVideo) {
			this.closeVideoPopup();
		}
	};

	fetchVideos = async () => {
		try {
			this.setState({loading: true, error: null});

			// Build base URL
			let baseUrl = getBaseUrl();
			const url = `${baseUrl}/api/v1/contact_us/instructional-videos?platformType=${isCordova() ? 'CORDOVA' : ''}`;

		// Make axios request
		const response = await axios.post(url, {
			url: '/l8k7ud3/training/'
		}, {
			headers: {
				'Content-Type': 'application/json',
				'x-auth-token': localStorage.token || ''
			}
		});

			const videos = response.data?.data || response.data;

			if (videos && Array.isArray(videos)) {
				this.setState({videos, loading: false});
			} else {
				this.setState({error: 'Failed to load videos', loading: false});
			}
		} catch (error) {
			console.error('Error fetching videos:', error);
			this.setState({error: 'Failed to load videos. Please try again later.', loading: false});
		}
	};

	openVideoPopup = (videoSrc, videoData) => {
		// Build video URL
		const videoUrl = getBaseUrl() + videoSrc;
		
		this.setState({selectedVideo: videoUrl, selectedVideoData: videoData}, () => {
			this.setupVideoHandlers();
		});
	};

	setupVideoHandlers = () => {
		// Setup video handling after state update
		setTimeout(() => {
			if (this.videoRef.current) {
				const video = this.videoRef.current;
				
				// Prevent fullscreen on play
				video.addEventListener('webkitbeginfullscreen', (e) => {
					e.preventDefault();
					video.webkitExitFullscreen();
				});
				
				// Handle presentation mode changes
				video.addEventListener('webkitpresentationmodechanged', (e) => {
					if (video.webkitPresentationMode === 'fullscreen') {
						video.webkitSetPresentationMode('inline');
					}
				});
				
				// Set additional properties for inline playback
				video.setAttribute('webkit-playsinline', 'true');
				video.setAttribute('playsinline', 'true');
				video.setAttribute('x-webkit-airplay', 'allow');
				
				// Force inline mode
				if (video.webkitSetPresentationMode) {
					video.webkitSetPresentationMode('inline');
				}
			}
		}, 100);
	};

	closeVideoPopup = () => {
		this.setState({selectedVideo: null, selectedVideoData: null});
	};

	close = () => {
		this.closeVideoPopup();
		window.popupController.hide(TrainingVideosPopup);
	};

	getImageUrl = (imgSrc) => {
		if (!imgSrc) return '';
		// If it's already an absolute URL, return as is
		if (imgSrc.startsWith('http://') || imgSrc.startsWith('https://')) {
			return imgSrc;
		}
		// If it's a relative path, prepend the marketing site URL
		if (imgSrc.startsWith('/')) {
			return `https://logrx.com${imgSrc}`;
		}
		return imgSrc;
	};

	filterVideos = () => {
		const {videos, searchTerm} = this.state;
		if (!searchTerm.trim()) {
			return videos;
		}
		const searchLower = searchTerm.toLowerCase();
		return videos.filter(video => 
			video.title.toLowerCase().includes(searchLower)
		);
	};

	handleSearchChange = (value) => {
		this.setState({searchTerm: value});
	};

	renderVideos = () => {
		if (this.state.loading) {
			return (
				<Label dark center>
					Loading videos...
				</Label>
			);
		}

		if (this.state.error) {
			return (
				<div>
					<Label dark center>
						{this.state.error}
					</Label>
					<PrimaryButton onClick={this.fetchVideos} className="center">
						Retry
					</PrimaryButton>
				</div>
			);
		}

		const filteredVideos = this.filterVideos();

		if (!this.state.videos || this.state.videos.length === 0) {
			return (
				<Label dark center>
					No videos available
				</Label>
			);
		}

		if (filteredVideos.length === 0) {
			return (
				<Label dark center>
					No videos found matching "{this.state.searchTerm}"
				</Label>
			);
		}

		return (
			<div className="video-container">
				{filteredVideos.map((video, index) => (
					<div
						key={index}
						className="video-card"
						onClick={() => this.openVideoPopup(video.streamSrc, video)}
					>
						<img
							src={this.getImageUrl(video.imgSrc)}
							alt={video.title}
						/>
						<PrimaryButton className="video-card-button" onClick={() => this.openVideoPopup(video.streamSrc, video)}
						>
							{video.title}
						</PrimaryButton>
					</div>
				))}
			</div>
		);
	};

	render() {
		return (
			<>
				<PopupComponent
					{...this.props}
					opened={this.props.opened}
					tabletFullscreen={true}
					className="g-training-videos-popup"
				>
					<Page>
						<div className="training-videos-header">
							<Label large dark bold>
								Training Videos
							</Label>
							<div className="close-button" onClick={this.close}>
								&times;
							</div>
						</div>
						<div className="search-container">
							<Input
								placeholder="Search videos..."
								onChange={this.handleSearchChange}
								iconName="search"
								center
							/>
						</div>
						{this.renderVideos()}
						{this.state.selectedVideo && (
							<div
								className="video-popup-overlay"
								onClick={(e) => {
									if (e.target.classList.contains('video-popup-overlay')) {
										this.closeVideoPopup();
									}
								}}
							>
								<div className="video-popup-container">
									<div className="video-iframe-container">
										<video
											playsInline={true}
											webkit-playsinline={true}
											x-webkit-airplay="allow"
											controls={true}
											preload="auto"
											poster={this.state.selectedVideoData ? this.getImageUrl(this.state.selectedVideoData?.imgSrc) : undefined}
											className="training-video"
											ref={this.videoRef}
											style={{
												width: '100%',
												height: '100%',
												position: 'absolute',
												top: 0,
												left: 0
											}}
										>
											<source src={this.state.selectedVideo} type="video/mp4"/>
										</video>
									</div>
								</div>
							</div>
						)}
					</Page>
				</PopupComponent>

				{/* Video Popup Overlay */}

			</>
		);
	}
}
